import { SELECTION_UNIT_KG } from "@/constants/misc"
import type { CouponDetail, CouponCalculationResult, CouponItemDiscount } from "./types"
import type { ValidatedCartItem } from "@/lib/cart/types"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isNonEmptyArray<T>(arr: T[] | null | undefined): arr is T[] {
  return Array.isArray(arr) && arr.length > 0
}

function getLastElement<T>(arr: T[]): T | null {
  return arr.length > 0 ? arr[arr.length - 1] : null
}

function getApplicableItemsForCoupon(coupon: CouponDetail, cartItems: ValidatedCartItem[]): ValidatedCartItem[] {
  if (!isNonEmptyArray(coupon.itemIds)) return []
  return cartItems.filter((item) => coupon.itemIds!.includes(item.productId))
}

/**
 * Returns the per-unit price for an item, selecting kg or piece price based on
 * the requested unit. Falls back to item.price if the specific price is missing.
 * TODO: extend with Calii+ specific pricing when those fields are available.
 */
function getCorrespondingPricePerUnit(
  item: ValidatedCartItem,
  unit: string,
  _isCaliiPlusMember: boolean,
): number {
  if (unit === SELECTION_UNIT_KG && item.pricePerKg != null) return item.pricePerKg
  if (item.pricePerPiece != null) return item.pricePerPiece
  return item.price
}

function computeItemTotalWithoutCoupon(item: ValidatedCartItem, isCaliiPlusMember: boolean): number {
  return (getCorrespondingPricePerUnit(item, item.unit, isCaliiPlusMember) || 0) * (item.quantity || 0)
}

function computeCouponDiscountForItem(coupon: CouponDetail, item: ValidatedCartItem, isCaliiPlusMember: boolean): number {
  const totalWithoutCoupon = computeItemTotalWithoutCoupon(item, isCaliiPlusMember)

  if (coupon.couponType === "percent_off") {
    return (coupon.value * totalWithoutCoupon) / 100
  }

  if (coupon.couponType === "absolute") {
    const pricePerUnit = getCorrespondingPricePerUnit(item, coupon.unit, isCaliiPlusMember)
    const equivalentPercent = Math.min((100 * coupon.value) / pricePerUnit, 100)
    return ((100 - equivalentPercent) * totalWithoutCoupon) / 100
  }

  if (coupon.couponType === "get_x_items_while_paying_for_y_items") {
    const pricePerUnit = getCorrespondingPricePerUnit(item, item.unit, isCaliiPlusMember)
    const totalWithCoupon =
      Math.floor(item.quantity / coupon.valueX) * (pricePerUnit * coupon.valueY) +
      (item.quantity % coupon.valueX) * pricePerUnit
    return totalWithoutCoupon - totalWithCoupon
  }

  if (coupon.couponType === "get_x_items_for_price_y") {
    const pricePerUnit = getCorrespondingPricePerUnit(item, item.unit, isCaliiPlusMember)
    const totalWithCoupon =
      Math.floor(item.quantity / coupon.valueX) * coupon.valueY +
      (item.quantity % coupon.valueX) * pricePerUnit
    return totalWithoutCoupon - totalWithCoupon
  }

  return 0
}

function applyItemSpecificCouponToItem(
  coupon: CouponDetail,
  item: ValidatedCartItem,
  isCaliiPlusMember: boolean,
  alreadyUsedAmount: number,
): CouponItemDiscount {
  let couponDiscount = Math.max(computeCouponDiscountForItem(coupon, item, isCaliiPlusMember), 0)
  let hasReachedMaxSavings = false

  let maxDiscount = coupon.maxDiscount
  if (coupon.maxQuantity > 0) {
    const pricePerUnit = getCorrespondingPricePerUnit(item, coupon.correspondingUnitForMaxQuantity, isCaliiPlusMember)
    if (coupon.couponType === "absolute") {
      maxDiscount = coupon.maxQuantity * (pricePerUnit - coupon.value)
    }
    if (coupon.couponType === "percent_off") {
      maxDiscount = coupon.maxQuantity * (pricePerUnit * coupon.value) / 100
    }
  }

  if (maxDiscount > 0 && maxDiscount < couponDiscount + alreadyUsedAmount) {
    couponDiscount = Math.max(maxDiscount - alreadyUsedAmount, 0)
    hasReachedMaxSavings = true
  }

  const totalWithoutCoupon = computeItemTotalWithoutCoupon(item, isCaliiPlusMember)
  return {
    totalWithoutCoupon,
    couponDiscount,
    totalWithCoupon: Math.max(totalWithoutCoupon - couponDiscount, 0),
    coupon,
    hasReachedMaxSavings,
  }
}

function getGlobalAmountOffUniqueCoupons(coupons: CouponDetail[]): CouponDetail[] {
  const result: CouponDetail[] = []
  for (const coupon of coupons) {
    if (isNonEmptyArray(coupon.itemIds) || coupon.couponType !== "amount_off") continue
    const isRepeat = result.some((c) => c.name === coupon.name || c.id === coupon.id)
    const isDuplicateCaliiPlusStep =
      coupon.isCaliiPlusMonthlySequence &&
      coupon.caliiPlusStepNumber != null &&
      result.some((c) => c.caliiPlusStepNumber === coupon.caliiPlusStepNumber)
    if (!isRepeat && !isDuplicateCaliiPlusStep) {
      result.push(coupon)
    }
  }
  return result
}

// ---------------------------------------------------------------------------
// Main function
// ---------------------------------------------------------------------------

export function getResultOfApplyingCouponsToOrder(
  coupons: CouponDetail[],
  cartItems: ValidatedCartItem[],
  isCaliiPlusMember: boolean,
  deliveryFee: number,
  freeDeliveryMin: number | null,
  isPickup = false,
): CouponCalculationResult {
  const activeCoupons = coupons.filter((c) => c != null)
  const itemDiscounts: Record<string, CouponItemDiscount> = {}

  // Item-specific: absolute
  for (const coupon of activeCoupons.filter((c) => isNonEmptyArray(c.itemIds) && c.couponType === "absolute")) {
    let alreadyUsedAmount = 0
    for (const item of getApplicableItemsForCoupon(coupon, cartItems)) {
      itemDiscounts[item.productId] = applyItemSpecificCouponToItem(coupon, item, isCaliiPlusMember, alreadyUsedAmount)
      alreadyUsedAmount += itemDiscounts[item.productId].couponDiscount
    }
  }

  // Item-specific: get_x_items_for_price_y
  for (const coupon of activeCoupons.filter((c) => isNonEmptyArray(c.itemIds) && c.couponType === "get_x_items_for_price_y")) {
    for (const item of getApplicableItemsForCoupon(coupon, cartItems)) {
      if (!itemDiscounts[item.productId]) {
        itemDiscounts[item.productId] = applyItemSpecificCouponToItem(coupon, item, isCaliiPlusMember, 0)
      }
    }
  }

  // Item-specific: get_x_items_while_paying_for_y_items
  for (const coupon of activeCoupons.filter((c) => isNonEmptyArray(c.itemIds) && c.couponType === "get_x_items_while_paying_for_y_items")) {
    for (const item of getApplicableItemsForCoupon(coupon, cartItems)) {
      if (!itemDiscounts[item.productId]) {
        itemDiscounts[item.productId] = applyItemSpecificCouponToItem(coupon, item, isCaliiPlusMember, 0)
      }
    }
  }

  // Item-specific: amount_off
  for (const coupon of activeCoupons.filter((c) => isNonEmptyArray(c.itemIds) && c.couponType === "amount_off")) {
    let remainingAmount = coupon.value
    const matchingItems = getApplicableItemsForCoupon(coupon, cartItems)
    const matchingItemsRawTotal = matchingItems.reduce(
      (sum, item) => sum + computeItemTotalWithoutCoupon(item, isCaliiPlusMember),
      0,
    )
    if (matchingItemsRawTotal >= (coupon.minTotalRequiredInApplicableItems ?? 0)) {
      for (const item of matchingItems) {
        if (!itemDiscounts[item.productId] && remainingAmount > 0) {
          const totalWithoutCoupon = computeItemTotalWithoutCoupon(item, isCaliiPlusMember)
          const couponDiscount = Math.min(remainingAmount, totalWithoutCoupon)
          remainingAmount -= couponDiscount
          itemDiscounts[item.productId] = {
            totalWithoutCoupon,
            couponDiscount,
            totalWithCoupon: Math.max(totalWithoutCoupon - couponDiscount, 0),
            coupon,
            hasReachedMaxSavings: remainingAmount < 0.0001,
          }
        }
      }
    }
  }

  // Item-specific: percent_off
  for (const coupon of activeCoupons.filter((c) => isNonEmptyArray(c.itemIds) && c.couponType === "percent_off")) {
    let alreadyUsedAmount = 0
    for (const item of getApplicableItemsForCoupon(coupon, cartItems)) {
      if (!itemDiscounts[item.productId]) {
        itemDiscounts[item.productId] = applyItemSpecificCouponToItem(coupon, item, isCaliiPlusMember, alreadyUsedAmount)
        alreadyUsedAmount += itemDiscounts[item.productId].couponDiscount
      }
    }
  }

  const cartSubtotalWithoutCoupons = cartItems.reduce(
    (sum, item) => sum + computeItemTotalWithoutCoupon(item, isCaliiPlusMember),
    0,
  )
  const itemSpecificDiscount = Object.values(itemDiscounts).reduce((sum, d) => sum + d.couponDiscount, 0)
  const cartSubtotalWithCoupons = cartSubtotalWithoutCoupons - itemSpecificDiscount

  // Global: amount_off
  let globalCouponDiscounts = 0
  for (const coupon of getGlobalAmountOffUniqueCoupons(activeCoupons)) {
    if (cartSubtotalWithCoupons >= (coupon.minSubtotalNecessary ?? 0)) {
      globalCouponDiscounts += coupon.value
    }
  }

  let totalAfterGlobalCoupons = Math.max(0, cartSubtotalWithCoupons - globalCouponDiscounts)

  // Global: percent_off (last one wins)
  const globalPercentOffCoupon = getLastElement(
    activeCoupons.filter((c) => !isNonEmptyArray(c.itemIds) && c.couponType === "percent_off"),
  )
  if (globalPercentOffCoupon && cartSubtotalWithCoupons >= (globalPercentOffCoupon.minSubtotalNecessary ?? 0)) {
    let couponDiscount = (globalPercentOffCoupon.value * totalAfterGlobalCoupons) / 100
    if (globalPercentOffCoupon.maxDiscount > 0) {
      couponDiscount = Math.min(globalPercentOffCoupon.maxDiscount, couponDiscount)
    }
    globalCouponDiscounts += couponDiscount
  }

  totalAfterGlobalCoupons = Math.max(0, cartSubtotalWithCoupons - globalCouponDiscounts)
  globalCouponDiscounts = cartSubtotalWithCoupons - totalAfterGlobalCoupons

  // Delivery
  let deliveryAmount = deliveryFee
  if (isPickup || (freeDeliveryMin != null && cartSubtotalWithCoupons >= freeDeliveryMin)) {
    deliveryAmount = 0
  } else {
    const deliveryCoupon = getLastElement(activeCoupons.filter((c) => c.isFreeDelivery))
    if (deliveryCoupon && cartSubtotalWithCoupons >= (deliveryCoupon.minSubtotalNecessary ?? 0)) {
      if (!isNonEmptyArray(deliveryCoupon.itemIds) || isNonEmptyArray(getApplicableItemsForCoupon(deliveryCoupon, cartItems))) {
        deliveryAmount = 0
      }
    }
  }

  // Min cart amount override
  let minCartAmountOverride: number | null = null
  const minCartAmountCoupon = getLastElement(
    activeCoupons.filter((c) => c.minCartAmountOverride != null),
  )
  if (minCartAmountCoupon) {
    if (!isNonEmptyArray(minCartAmountCoupon.itemIds) || isNonEmptyArray(getApplicableItemsForCoupon(minCartAmountCoupon, cartItems))) {
      minCartAmountOverride = minCartAmountCoupon.minCartAmountOverride
    }
  }

  const totalCouponDiscounts =
    globalCouponDiscounts +
    Object.values(itemDiscounts).reduce((sum, d) => sum + d.couponDiscount, 0)

  return {
    itemDiscounts,
    deliveryAmount,
    globalCouponDiscounts,
    cartSubtotalWithoutCoupons,
    cartSubtotalWithCoupons,
    minCartAmountOverride,
    totalCouponDiscounts,
  }
}
